import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkflowCompletionVisual } from '../workflowCompletion/workflowCompletion.visual';
import workflow from './createModelPortfolioWorkflow.workflow.json';
import { updateNextWorkflowStep, updatePreviousWorkflowStep } from '../../../util/hardCodedWorkflow';
import { WorkflowContext, WorkflowDataType } from '../workflowCompletion/workflowCompletion';
import { ConfirmationModal } from '../../3-pattern/confirmationDialog/confirmationDialog';
import { ModelPortfolioData } from '../workflowCompletion/subSteps';

export const FIELDS_TO_COMPARE = [
  'themeId',
  'theme',
  'translatedName',
  'translatedPortfolioDescription',
  'translatedInvestmentObjective',
  'translatedLiquidity',
  'translatedPortfolioDisclaimer',
  'riskReturnLevel',
  'riskProfileName',
  'securities',
  'strategy',
  'children',
  'suggestable',
  'zeroPercentCash',
  'lowThresholdScore',
  'suggestedMinScore',
  'suggestedMaxScore',
  'forecastedRateOfReturn',
  'forecastedStandardDeviation',
  'targetAllocations',
  'url',
] as const;

let lastComparison: {
  currentValues: any;
  originalValues: any;
  result: boolean;
} | null = null;

const normalizeValue = (value: any): any => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Check for empty HTML content
    if (trimmed === '' || trimmed === '<p><br></p>' || trimmed === '<p><br/></p>' || trimmed === '<p></p>') {
      return null;
    }
    return trimmed;
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length === 0 ? null : value;
    }

    // Handle TranslatedString objects
    if ('en' in value && 'fr' in value) {
      const hasContent = (value.en?.trim() && value.en?.trim() !== '<p><br></p>' && value.en?.trim() !== '<p><br/></p>' && value.en?.trim() !== '<p></p>')
        || (value.fr?.trim() && value.fr?.trim() !== '<p><br></p>' && value.fr?.trim() !== '<p><br/></p>' && value.fr?.trim() !== '<p></p>');
      return hasContent ? value : null;
    }

    const keys = Object.keys(value);
    if (keys.length === 0) {
      return null;
    }

    // Recursively normalize nested objects
    const normalizedObj: Record<string, any> = {};
    let hasNonEmptyValues = false;

    keys.forEach((key) => {
      const normalized = normalizeValue(value[key]);
      if (normalized !== null) {
        normalizedObj[key] = normalized;
        hasNonEmptyValues = true;
      }
    });

    return hasNonEmptyValues ? normalizedObj : null;
  }

  return value;
};

export const hasModelPortfolioChanges = (
  currentValues: Partial<ModelPortfolioData> | null,
  originalValues: Partial<ModelPortfolioData>,
): boolean => {
  if (lastComparison?.currentValues === currentValues
      && lastComparison?.originalValues === originalValues) {
    return lastComparison.result;
  }

  if (!currentValues) {
    lastComparison = { currentValues, originalValues, result: false };
    return false;
  }

  if (currentValues === originalValues) {
    lastComparison = { currentValues, originalValues, result: false };
    return false;
  }

  const result = FIELDS_TO_COMPARE.some((field) => {
    const currentValue = normalizeValue(currentValues[field as keyof ModelPortfolioData]);
    const originalValue = normalizeValue(originalValues[field as keyof ModelPortfolioData]);

    if (currentValue === originalValue) {
      return false;
    }

    if (currentValue === null || originalValue === null) {
      return currentValue !== originalValue;
    }

    if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
      if (field === 'securities' as keyof ModelPortfolioData
        || field === 'children' as keyof ModelPortfolioData
      ) {
        return currentValue.some((item, index) => {
          const otherItem = originalValue[index];
          if (!item || !otherItem) return true;
          if ('financialProduct' in item && 'financialProduct' in otherItem) {
            return item.id !== otherItem.id || item.percentage !== otherItem.percentage;
          }
          return false;
        });
      }
      if (field === 'targetAllocations' as keyof ModelPortfolioData) {
        const deletedAllocations = originalValue.filter((originalItem) => !currentValue.some((currentItem) => currentItem.secondaryAssetClass.id === originalItem.secondaryAssetClass.id));

        const hasChangedAllocations = originalValue.some((item) => {
          const currentItem = currentValue.find((i) => i.secondaryAssetClass.id === item.secondaryAssetClass.id);
          if (!currentItem) return false;
          return currentItem.min !== item.min || currentItem.max !== item.max;
        });

        const newAllocations = currentValue.filter((currentItem) => !originalValue.some((originalItem) => originalItem.secondaryAssetClass.id === currentItem.secondaryAssetClass.id));
        const hasNewAllocations = newAllocations.length > 0;

        return deletedAllocations.length > 0 || hasChangedAllocations || hasNewAllocations;
      }
      return currentValue.some((item, index) => {
        const otherItem = originalValue[index];
        return item !== otherItem;
      });
    }

    if (typeof currentValue === 'object' && typeof originalValue === 'object') {
      const currentKeys = Object.keys(currentValue);
      const originalKeys = Object.keys(originalValue);

      if (currentKeys.length !== originalKeys.length) {
        return true;
      }

      return currentKeys.some((key) => {
        const currentVal = (currentValue as Record<string, any>)[key];
        const originalVal = (originalValue as Record<string, any>)[key];
        return currentVal !== originalVal;
      });
    }

    return currentValue !== originalValue;
  });

  lastComparison = { currentValues, originalValues, result };
  return result;
};

const initialWorkflowCompletion = {
  id: '123',
  currentStep: workflow.steps[0],
  currentSubStep: workflow.steps[0].subSteps[0],
  workflow,
};

enum SegmentType {
  CHOOSE_PORTFOLIO_TYPE = 'CHOOSE_PORTFOLIO_TYPE',
  SELECT_PORTFOLIO_TEMPLATE = 'SELECT_PORTFOLIO_TEMPLATE',
  CREATE_PORTFOLIO_TEMPLATE = 'CREATE_PORTFOLIO_TEMPLATE',
}

const initialWorkflowData = {
  currentSegment: null,
  segmentHistory: [],
};

export const CreateModelPortfolioWorkflow = ({
  open,
  setOpen,
  objectId,
  refetch,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  objectId: string;
  refetch: () => void;
}) => {
  const { t } = useTranslation(['shared', 'modelPortfolios']);
  const [workflowData, setWorkflowData] = useState<WorkflowDataType>(initialWorkflowData);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [stepHistory, setStepHistory] = useState<any[]>([]);
  const [workflowCompletion, setWorkflowCompletion] = useState({
    objectId,
    ...initialWorkflowCompletion,
  });

  const resetWorkflow = () => {
    setWorkflowData(initialWorkflowData);
    setStepHistory([]);
    setWorkflowCompletion({
      objectId,
      ...initialWorkflowCompletion,
    });
  };

  const updateNext = () => {
    setStepHistory([...stepHistory, workflowCompletion.currentSubStep]);

    if (workflowData.currentSegment && workflowData.currentSegment !== SegmentType.CREATE_PORTFOLIO_TEMPLATE) {
      const { currentSegment, segmentHistory = [] } = workflowData;
      if (!segmentHistory.length || segmentHistory[segmentHistory.length - 1] !== currentSegment) {
        setWorkflowData({
          ...workflowData,
          segmentHistory: [...segmentHistory, currentSegment],
        });
      }
    }

    updateNextWorkflowStep(workflowCompletion, setWorkflowCompletion, () => {
      refetch();
      setOpen(false);
      resetWorkflow();
    });
  };

  const handleModelPortfolioSectionNavigation = () => {
    const { segmentHistory = [], currentSegment } = workflowData;

    if (workflowData.currentSegment === SegmentType.CREATE_PORTFOLIO_TEMPLATE) {
      setWorkflowData({
        ...workflowData,
        currentSegment: SegmentType.SELECT_PORTFOLIO_TEMPLATE,
        segmentHistory: segmentHistory
          .filter((segment, index) => segmentHistory.indexOf(segment) === index)
          .filter((segment) => segment !== SegmentType.CREATE_PORTFOLIO_TEMPLATE),
      });
      return;
    }

    if (currentSegment === SegmentType.SELECT_PORTFOLIO_TEMPLATE) {
      setWorkflowData({
        ...workflowData,
        segmentHistory: [],
        currentSegment: SegmentType.CHOOSE_PORTFOLIO_TYPE,
      });
      return;
    }

    if (segmentHistory.length > 0) {
      const newHistory = Array.from(new Set(segmentHistory));
      const previousSegment = newHistory.pop();

      setWorkflowData({
        ...workflowData,
        segmentHistory: newHistory,
        currentSegment: previousSegment,
      });
      return;
    }

    setWorkflowData(initialWorkflowData);
  };

  const handleWorkflowNavigation = () => {
    if (stepHistory.length > 0) {
      const previousStep = stepHistory[stepHistory.length - 1];
      setStepHistory(stepHistory.slice(0, -1));
      setWorkflowCompletion({
        ...workflowCompletion,
        currentSubStep: previousStep,
      });
    } else if (workflowCompletion.currentSubStep?.id !== workflow.steps[0]?.subSteps[0]?.id) {
      updatePreviousWorkflowStep(workflowCompletion, setWorkflowCompletion);
    }
  };

  const updatePrevious = () => {
    const { currentSubStep } = workflowCompletion;

    if ((currentSubStep?.type === 'CREATE_MODEL_PORTFOLIO')) {
      handleModelPortfolioSectionNavigation();
      return;
    }

    handleWorkflowNavigation();
  };

  const closeModelPortfolioWorkflow = () => {
    const hasModelPortfolioDetailsChanges = workflowData?.modelPortfolioDetails?.originalValues && hasModelPortfolioChanges(
      workflowData.modelPortfolioDetails,
      workflowData.modelPortfolioDetails.originalValues,
    );

    const hasTemplateCreationInProgress = workflowData?.modelPortfolioDetails?.templateCreationInProgress;

    if (hasModelPortfolioDetailsChanges || hasTemplateCreationInProgress) {
      setShowCancelDialog(true);
    } else {
      handleConfirmClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCancelDialog(false);
    setOpen(false);
    setWorkflowData({});
    setStepHistory([]);
    setWorkflowCompletion({
      objectId,
      ...initialWorkflowCompletion,
    });
  };

  const navigateToSegment = (segment: string) => {
    const { currentSegment } = workflowData;

    if (currentSegment !== segment) {
      const newHistory = currentSegment && currentSegment !== SegmentType.CREATE_PORTFOLIO_TEMPLATE
        ? [...(workflowData.segmentHistory || []), currentSegment]
        : workflowData.segmentHistory || [];

      setWorkflowData({
        ...workflowData,
        currentSegment: segment as SegmentType,
        segmentHistory: newHistory,
      });
    }
  };

  return (
    <WorkflowContext.Provider
      value={{
        workflowData,
        setWorkflowData,
        navigateToSegment,
      }}
    >
      <WorkflowCompletionVisual
        workflowCompletion={workflowCompletion}
        open={open}
        onClose={closeModelPortfolioWorkflow}
        onNext={updateNext}
        previousStep={updatePrevious}
        loading={false}
      />
      <ConfirmationModal
        open={showCancelDialog}
        title={t('shared:wizardTitle', { wizardTitle: t('modelPortfolios:createModelPortfolio.label') })}
        bodyText={t('shared:wizardInterruptionMsg')}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowCancelDialog(false)}
        testIdPrefix="create-model-portfolio-workflow-cancel-dialog"
      />
    </WorkflowContext.Provider>
  );
};
